import { Form, Formik, FormikErrors, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { MdEuroSymbol } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import buildingsApi from '../../../APIs/buildings';
import techniciansJobApi from '../../../APIs/technicians';
import { ReactComponent as CloseSVG } from '../../../assets/icons/cross.svg';
import ConfirmationModalTechnician from '../../../components/ConfirmationModal/ConfirmationModal';
import escConstants from '../../../constants';
import {
  Alert,
  BrowseFile,
  Button,
  ColorInput,
  Divider,
  ErrorMessage,
  Flex,
  FormContainer,
  Input,
  ListImage,
  Radio,
  TextArea,
  Typography,
} from '../../../designComponents';
import SelectDropDown from '../../../designComponents/SelectDropdown';
import { CREATE_JOBS_ROUTE } from '../../../routes/routePaths';
import { RootState } from '../../../store/selectors';
import { PaginationType } from '../../../types';
import { FileWithKeys, requestAPI, uploadFilesToS3 } from '../../../utils';
import { handleKeyDownForNumberOnly } from '../../../utils/keyboardHandlerEvents';
import { BuildingType } from '../../Building';
import { drawingMaterialTypes } from './materialTypes';
import { paperSizes } from './paperSizes';
import {
  AdditionalReqContainerStyled,
  CloseAdditionalWrapperStyled,
  CreateTechnicianJobsContainerStyled,
} from './style';
import {
  createTechnicianJobsValidation,
  updateTechnicianJobsValidation,
} from './validation';

type AdditionalRequirementType = {
  title: string;
  value: string;
  _id: string;
}[];

type InitialValuesType = {
  jobTitle: string;
  notes: string;
  buildingName: string;
  buildingId: string;
  buildingAddress: string;
  sizeOfDrawing: string;
  frameColor: string;
  logoFiles: File[];
  logoResolution: string;
  drawingMaterial: string;
  roundedEdge: boolean;
  pricing: string;
  shippingAddress: string;
  logo?: string[];
  additionalRequirement: AdditionalRequirementType;
};

const MAX_PRICE = 99999999;
const MAX_NUMBER_OF_LOGO = 4;

const AddTechnicianJob = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formikRef = useRef<FormikProps<InitialValuesType>>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [priceLimitError, setPriceLimitError] = useState('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const buildings: BuildingType[] = useSelector(
    (state: RootState) => state.buildings.data,
  );

  const technicianJobsDetails = useSelector(
    (state: RootState) => state.technicianJobsDetails,
  );

  const editString = searchParams.get('edit');
  const editJobId = editString ? editString.split('-')[1] : '';

  let initialState: InitialValuesType = {
    buildingAddress: '',
    buildingId: '',
    buildingName: '',
    drawingMaterial: '',
    frameColor: '#f3d10e',
    jobTitle: '',
    logoResolution: '',
    logoFiles: [],
    notes: '',
    roundedEdge: false,
    sizeOfDrawing: '',
    pricing: '100',
    shippingAddress: '',
    additionalRequirement: [],
  };

  if (technicianJobsDetails && technicianJobsDetails?._id === editJobId) {
    initialState = {
      jobTitle: technicianJobsDetails.jobTitle || '',
      notes: technicianJobsDetails.notes || '',
      buildingId: technicianJobsDetails.building?._id || '',
      buildingName: technicianJobsDetails.building?.buildingName || '',
      buildingAddress: technicianJobsDetails.building?.buildingAddress || '',
      sizeOfDrawing: technicianJobsDetails?.drawingSize || '',
      logo: technicianJobsDetails?.logo || [],
      logoFiles: [],
      logoResolution: technicianJobsDetails.logoResolution || '',
      drawingMaterial: technicianJobsDetails.drawingMaterialType || '',
      pricing: technicianJobsDetails.pricing || '',
      shippingAddress: technicianJobsDetails.shippingAddress || '',
      frameColor: technicianJobsDetails.frameColor || '',
      roundedEdge: technicianJobsDetails.roundedEdge || false,
      additionalRequirement: technicianJobsDetails?.additionalRequirement || [],
    };
  }
  useEffect(() => {
    if (editString?.includes('job-')) {
      const getTechnicianJob = async (id: string) => {
        try {
          setLoading(true);
          await requestAPI(techniciansJobApi.getTechnicianJobsDetail(id));
        } catch (error) {
          console.log(error);
        }
        setLoading(false);
      };
      if (!technicianJobsDetails?._id) {
        getTechnicianJob(editJobId);
      }
    }
  }, [searchParams]);

  const handleGetBuildings = async (myParams: PaginationType) => {
    try {
      await requestAPI(
        buildingsApi.getBuildings({
          page: myParams.page,
          limit: myParams.limit,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    handleGetBuildings({ page: 1, limit: 100 });
  }, []);

  const handleCreateJobs = async (values: InitialValuesType) => {
    try {
      setErrorMessage('');
      setLoading(true);
      let fileWithKeys: FileWithKeys[] = [];
      if (files && files.length > 0) {
        fileWithKeys = (await uploadFilesToS3(files)) as FileWithKeys[];
      }
      if (fileWithKeys && fileWithKeys.length === files.length) {
        await requestAPI(
          techniciansJobApi.createTechnician({
            jobTitle: values.jobTitle,
            notes: values.notes,
            building: values.buildingId,
            drawingSize: values.sizeOfDrawing,
            logo: fileWithKeys.map((fwk) => fwk.key),
            logoResolution: values.logoResolution,
            drawingMaterialType: values.drawingMaterial,
            pricing: values.pricing,
            shippingAddress: values.shippingAddress,
            frameColor: values.frameColor,
            roundedEdge: values.roundedEdge,
            additionalRequirement: values.additionalRequirement.map((data) => {
              return {
                title: data.title,
                value: data.value,
              };
            }),
          }),
        );
      }
      navigate(CREATE_JOBS_ROUTE);
      toast(escConstants.toastMessages.technicianJobCreated);
    } catch (error: any) {
      if (typeof error === 'string') {
        setErrorMessage(error);
      }
      if (error.error && typeof error.error === 'string') {
        setErrorMessage(error.error);
      }
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  const handleUpdateJobs = async (values: InitialValuesType) => {
    try {
      setErrorMessage('');
      setLoading(true);
      let fileWithKeys: FileWithKeys[] = [];
      if (files && files.length > 0) {
        fileWithKeys = (await uploadFilesToS3(files)) as FileWithKeys[];
      }
      if (fileWithKeys && fileWithKeys.length === files.length) {
        const uploadedFileKeys = fileWithKeys.map((fwk) => fwk.key);
        await requestAPI(
          techniciansJobApi.updateTechnician(editJobId, {
            jobTitle: values.jobTitle,
            notes: values.notes,
            building: values.buildingId,
            drawingSize: values.sizeOfDrawing,
            logo:
              Array.isArray(values?.logo) && values?.logo?.length > 0
                ? [...values.logo, ...uploadedFileKeys]
                : uploadedFileKeys,
            logoResolution: values.logoResolution,
            drawingMaterialType: values.drawingMaterial,
            pricing: values.pricing,
            shippingAddress: values.shippingAddress,
            frameColor: values.frameColor,
            roundedEdge: values.roundedEdge,
            additionalRequirement: values?.additionalRequirement?.map(
              (data) => {
                return {
                  title: data?.title,
                  value: data?.value,
                };
              },
            ),
          }),
        );
      }
      navigate(`${CREATE_JOBS_ROUTE}/details/technician/${editJobId}`);
      toast(escConstants.toastMessages.technicianJobUpdated);
    } catch (error: any) {
      if (typeof error === 'string') {
        setErrorMessage(error);
      }
      if (error.error && typeof error.error === 'string') {
        setErrorMessage(error.error);
      }
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  const handleAddAdditionalRequirement = () => {
    if (formikRef && formikRef.current) {
      const additionalReq: AdditionalRequirementType =
        formikRef.current.values.additionalRequirement;
      const newReq = {
        title: '',
        value: '',
        _id: String(additionalReq.length + 1),
      };
      formikRef.current.setFieldValue('additionalRequirement', [
        ...additionalReq,
        newReq,
      ]);
    }
  };

  const handleRemoveAdditionalRequirement = (idx: number) => {
    if (formikRef && formikRef.current) {
      const additionalReq: AdditionalRequirementType =
        formikRef.current.values.additionalRequirement;

      formikRef.current.setFieldValue('additionalRequirement', [
        ...additionalReq.filter((_, index) => index !== idx),
      ]);
    }
  };

  return (
    <CreateTechnicianJobsContainerStyled>
      <Typography variant="title" className="heading">
        Create Job for Technician and Escape Plan
      </Typography>
      {errorMessage ? <Alert type="error" text={errorMessage} /> : null}
      <Formik
        innerRef={formikRef}
        onSubmit={editJobId ? handleUpdateJobs : handleCreateJobs}
        initialValues={initialState}
        validationSchema={
          editJobId
            ? updateTechnicianJobsValidation(
              (technicianJobsDetails?.logo || [])?.length > 0,
            )
            : createTechnicianJobsValidation
        }
        enableReinitialize
      >
        {({ values, setFieldValue, touched, errors }) => {
          return (
            <Form>
              <FormContainer
                label="Job Title"
                error={touched.jobTitle ? errors.jobTitle : ''}
              >
                <Input
                  placeholder="Job Title"
                  value={values.jobTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    setFieldValue('jobTitle', value);
                  }}
                />
              </FormContainer>
              <FormContainer
                label="Notes"
                error={touched.notes ? errors.notes : ''}
              >
                <TextArea
                  value={values.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    const { value } = e.target;
                    setFieldValue('notes', value);
                  }}
                  placeholder="Please add notes"
                  showCount
                  maxLength={800}
                />
              </FormContainer>
              <FormContainer
                label="Building Name"
                error={touched.buildingName ? errors.buildingName : ''}
              >
                <SelectDropDown
                  placeholder="Please select a name of building"
                  selected={values.buildingId}
                  onSelect={(value: string, record: any) => {
                    setFieldValue('buildingId', value);
                    setFieldValue('buildingName', record?.buildingName);
                    setFieldValue('buildingAddress', record?.buildingAddress);
                  }}
                  options={buildings.map((build) => ({
                    label: build.buildingName,
                    value: build._id,
                    record: build,
                  }))}
                />
              </FormContainer>
              <FormContainer
                label="Address of Property"
                error={touched.buildingAddress ? errors.buildingAddress : ''}
              >
                <Input readOnly value={values.buildingAddress} />
              </FormContainer>

              <Typography className="selection-notes">
                Please select the pertinent details for the building's
                evacuation plans, intended for submission to the CAD Technician
                or contractor for the creation of a CAD file.
              </Typography>

              <FormContainer
                label="Size of Drawing"
                error={touched.sizeOfDrawing ? errors.sizeOfDrawing : ''}
              >
                <SelectDropDown
                  placeholder="Please select a size of drawing"
                  selected={values.sizeOfDrawing}
                  onSelect={(value: string, _record: any) => {
                    setFieldValue('sizeOfDrawing', value);
                  }}
                  options={paperSizes}
                />
              </FormContainer>
              <FormContainer
                label="Select a Frame Color"
                error={touched.frameColor ? errors.frameColor : ''}
              >
                <ColorInput
                  label={values.frameColor}
                  value={values.frameColor}
                  onChange={(color) => {
                    setFieldValue('frameColor', color?.hex);
                  }}
                  hideAlpha
                />
              </FormContainer>
              <FormContainer
                label="Upload Logo"
                error={
                  touched.logoFiles &&
                  values.logoFiles.length === 0 &&
                  typeof errors.logoFiles === 'string'
                    ? errors.logoFiles
                    : ''
                }
              >
                <div className="browse-files">
                  <Typography
                    variant="caption"
                    fontColor="border"
                    className="upload-logo-svg"
                  >
                    You can upload a logo in either SVG, PNG, or JPG format, and
                    you also have the option to add multiple logos.
                  </Typography>
                  <BrowseFile
                    setFiles={(selectedfiles: File[]) => {
                      selectedfiles.splice(
                        MAX_NUMBER_OF_LOGO,
                        selectedfiles.length - MAX_NUMBER_OF_LOGO,
                      );

                      setFieldValue('logoFiles', selectedfiles);
                      setFiles(selectedfiles);
                    }}
                    files={files}
                    accepts=".jpg, .png, .jpeg, .svg"
                    multiple
                    showFileBrowser={
                      values.logoFiles.length < MAX_NUMBER_OF_LOGO
                    }
                  />
                </div>
              </FormContainer>
              <Flex>
                <ListImage
                  imagesURL={values?.logo?.map((val) => ({
                    id: val,
                    URL: val,
                  }))}
                  enableRemoveImage
                  onRemove={(id: string) => {
                    setFieldValue(
                      'logo',
                      values.logo?.filter((x) => x !== id),
                    );
                  }}
                />
              </Flex>
              <FormContainer
                label="Resolution of Logo"
                error={touched.logoResolution ? errors.logoResolution : ''}
              >
                <SelectDropDown
                  placeholder="Please select a resolution of logo"
                  selected={values.logoResolution}
                  onSelect={(value: string, _record: any) => {
                    setFieldValue('logoResolution', value);
                  }}
                  options={[
                    { label: '90 DPI', value: '90 DPI' },
                    { label: '130 DPI', value: '130 DPI' },
                    { label: '200 DPI', value: '200 DPI' },
                    { label: '300 DPI', value: '300 DPI' },
                  ]}
                />
              </FormContainer>
              <FormContainer
                label="Material Type of Drawing"
                error={touched.drawingMaterial ? errors.drawingMaterial : ''}
              >
                <SelectDropDown
                  placeholder="Please select a material type for drawing."
                  selected={values.drawingMaterial}
                  onSelect={(value: string, _record: any) => {
                    setFieldValue('drawingMaterial', value);
                  }}
                  options={drawingMaterialTypes}
                />
              </FormContainer>
              <FormContainer
                label="Rounded Edge"
                error={touched.roundedEdge ? errors.roundedEdge : ''}
              >
                <div style={{ display: 'flex', gap: '30px' }}>
                  <Radio
                    label="Yes"
                    name="rounded"
                    value="rounded"
                    checked={values.roundedEdge}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { checked } = e.target;
                      setFieldValue('roundedEdge', checked);
                    }}
                  />
                  <Radio
                    label="No"
                    name="rounded"
                    value="not-rounded"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { checked } = e.target;
                      setFieldValue('roundedEdge', !checked);
                    }}
                    checked={!values.roundedEdge}
                  />
                </div>
              </FormContainer>

              <FormContainer
                label="Pricing"
                error={touched.pricing ? errors.pricing : ''}
              >
                <>
                  <Input
                    iconLeft={<MdEuroSymbol />}
                    type="number"
                    placeholder="Enter your pricing for job"
                    value={values.pricing}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let { value } = e.target;

                      if (parseInt(value) > MAX_PRICE) {
                        value = `${MAX_PRICE}`;
                        setPriceLimitError(
                          `Pricing has max limit of ${MAX_PRICE}`,
                        );
                      } else {
                        setPriceLimitError('');
                      }
                      setFieldValue('pricing', value);
                    }}
                    max={MAX_PRICE}
                    onKeyDown={handleKeyDownForNumberOnly}
                  />
                  {priceLimitError && (
                    <Typography fontColor="dark" size={12}>
                      {priceLimitError}
                    </Typography>
                  )}
                </>
              </FormContainer>
              <FormContainer
                label="Shipping Address"
                error={touched.shippingAddress ? errors.shippingAddress : ''}
              >
                <Input
                  placeholder="Enter your shipping address"
                  value={values.shippingAddress}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    setFieldValue('shippingAddress', value);
                  }}
                />
              </FormContainer>

              <AdditionalReqContainerStyled
                $hasAdditional={values.additionalRequirement.length > 0}
              >
                {values?.additionalRequirement?.map((val, idx) => {
                  return (
                    <div key={`${val._id}-${idx}`}>
                      <CloseAdditionalWrapperStyled>
                        <CloseSVG
                          onClick={() => handleRemoveAdditionalRequirement(idx)}
                        />
                      </CloseAdditionalWrapperStyled>
                      <FormContainer
                        label={`Additional Requirement Title ${idx + 1}`}
                        error={
                          touched?.additionalRequirement &&
                          touched?.additionalRequirement[idx]?.title
                            ? errors?.additionalRequirement &&
                              (
                                errors?.additionalRequirement[
                                  idx
                                ] as FormikErrors<{
                                  title: string;
                                  value: string;
                                  id: string;
                                }>
                              )?.title
                            : ''
                        }
                      >
                        <Input
                          placeholder="Please enter a title"
                          value={values.additionalRequirement[idx].title}
                          onChange={(e) => {
                            const { value } = e.target;
                            const updatedValues = [
                              ...values.additionalRequirement,
                            ];
                            updatedValues[idx] = {
                              ...updatedValues[idx],
                              title: value,
                            };
                            setFieldValue(
                              'additionalRequirement',
                              updatedValues,
                            );
                          }}
                        />
                      </FormContainer>
                      <FormContainer
                        label={`Additional Requirement Description ${idx + 1}`}
                        error={
                          touched?.additionalRequirement &&
                          touched?.additionalRequirement[idx]?.value
                            ? errors?.additionalRequirement &&
                              (
                                errors?.additionalRequirement[
                                  idx
                                ] as FormikErrors<{
                                  title: string;
                                  value: string;
                                  id: string;
                                }>
                              )?.value
                            : ''
                        }
                      >
                        <TextArea
                          value={values.additionalRequirement[idx].value}
                          onChange={(e) => {
                            const { value } = e.target;
                            const updatedValues = [
                              ...values.additionalRequirement,
                            ];
                            updatedValues[idx] = {
                              ...updatedValues[idx],
                              value,
                            };
                            setFieldValue(
                              'additionalRequirement',
                              updatedValues,
                            );
                          }}
                          placeholder="Please enter your requirement"
                          showCount
                          maxLength={160}
                        />
                      </FormContainer>
                      <Divider />
                    </div>
                  );
                })}
                <Button
                  variant="gray"
                  size="sm"
                  onClick={handleAddAdditionalRequirement}
                  htmlType="button"
                >
                  Additional Requirement
                </Button>
              </AdditionalReqContainerStyled>
              <Button
                variant="secondary"
                isLoading={loading}
                size="lg"
                style={{
                  padding: '7px 30px',
                }}
                onClick={async () => {
                  if (formikRef.current) {
                    await formikRef.current.validateForm();
                    formikRef.current.setTouched({
                      additionalRequirement: [{ title: true }],
                      buildingAddress: true,
                      buildingId: true,
                      buildingName: true,
                      drawingMaterial: true,
                      frameColor: true,
                      jobTitle: true,
                      logo: true,
                      logoFiles: [{ name: true }],
                      logoResolution: true,
                      notes: true,
                      pricing: true,
                      roundedEdge: true,
                      shippingAddress: true,
                      sizeOfDrawing: true,
                    });
                    if (Object.keys(formikRef.current.errors).length > 0) {
                      return;
                    }
                    setIsOpen(true);
                  }
                }}
              >
                {editJobId ? 'Edit Job' : 'Create Job'}
              </Button>
              {Object.keys(errors).length > 0 &&
                Object.keys(touched).length >= Object.keys(errors).length && (
                  <ErrorMessage
                    text={'Some fields missing, please check and try again.'}
                  />
              )}
              {isOpen ? (
                <ConfirmationModalTechnician
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  message={`Are you sure want to ${editJobId ? 'update' : 'create'} a technician job?`}
                  title="Technician Job"
                  okText={editJobId ? 'Update' : 'Create'}
                  onOk={() => {
                    if (formikRef.current?.dirty) {
                      formikRef.current?.handleSubmit();
                    } else {
                      setIsOpen(false);
                      formikRef.current?.handleSubmit();
                    }
                  }}
                  loading={loading}
                />
              ) : (
                ''
              )}
            </Form>
          );
        }}
      </Formik>
    </CreateTechnicianJobsContainerStyled>
  );
};

export default AddTechnicianJob;
